import { useState, useRef, useEffect } from "react";
import {
  X, Trash2, Check, Bell, BellOff, Mail, Phone, Copy, Twitter, Linkedin, Facebook, Share, Lock, LockOpen, Sparkles
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { detectCategory } from "@/hooks/useBoardData";
import { playFocusSwooshSound } from "@/utils/sounds";
import { CategorySelector, PhoneContactChip } from "./BoardComponents";
import { EMAIL_NUDGES_ENABLED } from "@/config/featureFlags";

// Card Edit Modal
export const CardEditModal = ({ card, onSave, onClose, onShare, onDelete, nudgeSystem, userId, onShowNudgePermission, onOpenInstallHelp, onOpenLockScreen, isLockScreenFocus, isInDoingList }) => {
  const [content, setContent] = useState(card?.content || "");
  const [notes, setNotes] = useState(card?.notes || "");
  const [isCritical, setIsCritical] = useState(card?.isCritical || false);
  const [category, setCategory] = useState(card?.category || "action");
  const [manualCategory, setManualCategory] = useState(false);
  const [nudgeEnabled, setNudgeEnabled] = useState(card?.nudgeEnabled === true);

  const [linkedContact, setLinkedContact] = useState(card?.linkedContact || null);
  const [showContactInput, setShowContactInput] = useState(!!card?.linkedContact);
  const [contactName, setContactName] = useState(card?.linkedContact?.name || "");
  const [contactEmail, setContactEmail] = useState(card?.linkedContact?.email || "");
  const [contactPhone, setContactPhone] = useState(card?.linkedContact?.phone || "");

  useEffect(() => {
    if (!manualCategory && content.trim()) {
      const detected = detectCategory(content.trim());
      setCategory(detected);
    }
  }, [content, manualCategory]);

  const handleImportantToggle = () => {
    // Important marker and nudge state are now independent — users can
    // enable/disable each on its own without one flipping the other.
    setIsCritical(!isCritical);
  };

  const handleCategoryChange = (cat) => {
    setManualCategory(true);
    setCategory(cat);
  };

  const buildLinkedContact = () => {
    if (!contactName.trim() && !contactEmail.trim() && !contactPhone.trim()) {
      return null;
    }
    return {
      name: contactName.trim() || null,
      email: contactEmail.trim() || null,
      phone: contactPhone.trim() || null,
    };
  };

  const handleSave = () => {
    if (content.trim()) {
      const contact = buildLinkedContact();
      onSave({
        ...card,
        content: content.trim(),
        notes: notes.trim(),
        isCritical,
        category,
        linkedContact: contact,
        nudgeEnabled,
      });
    }
  };

  const handleNudgeToggle = async () => {
    const newNudgeState = !nudgeEnabled;

    // Only detour through the browser-push permission modal when push is
    // genuinely supported AND we haven't been granted yet. On iOS Safari
    // (non-PWA) push isn't supported at all — we still let the user flip
    // the toggle so handleSaveCard can schedule the email-only fallback.
    if (newNudgeState && nudgeSystem?.isSupported && !nudgeSystem.hasPermission) {
      if (onShowNudgePermission) {
        onShowNudgePermission(card.id, content.trim() || card.content);
      }
      return;
    }

    setNudgeEnabled(newNudgeState);
  };

  const handleRemoveContact = () => {
    setContactName("");
    setContactEmail("");
    setContactPhone("");
    setLinkedContact(null);
    setShowContactInput(false);
  };

  if (!card) return null;

  const isEmailTask = category === 'email';
  const isCallTask = category === 'phone';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-3 pt-6 pb-6 overflow-y-auto"
      style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 0))' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-4 w-full max-w-[380px] shadow-xl my-auto max-h-[90dvh] overflow-y-auto"
      >
        <h3 className="text-base font-semibold text-[#27272A] mb-2">Edit Card</h3>

        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-[#52525B] mb-1">
              Add a task
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className={`w-full rounded-xl p-2 text-sm border outline-none resize-none transition-all duration-200 ${
                isCritical
                  ? 'bg-yellow-100/70 border-amber-300'
                  : 'bg-[#FDFBF7] border-stone-200 focus:border-[#F58227]'
              }`}
              rows={1}
              autoFocus
              data-testid="edit-card-content-input"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#52525B] mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context..."
              className="w-full bg-[#FDFBF7] rounded-xl p-2 text-sm border border-stone-200 outline-none focus:border-[#F58227] resize-none"
              rows={1}
              data-testid="edit-card-notes-input"
            />
          </div>

          <button
            onClick={handleImportantToggle}
            className={`w-full flex items-center gap-2 p-2 rounded-xl border transition-all ${
              isCritical
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-[#FDFBF7] border-stone-200 text-stone-500 hover:border-amber-200 hover:bg-amber-50/50'
            }`}
            data-testid="critical-toggle-modal"
          >
            <span
              className={`text-xl font-bold leading-none ${isCritical ? 'text-amber-400' : ''}`}
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              *
            </span>
            <span className="text-xs font-medium">
              {isCritical ? "Important" : "Mark as important"}
            </span>
          </button>

          {!EMAIL_NUDGES_ENABLED && (
            <div
              className="w-full flex items-center gap-2 p-2 rounded-xl border border-stone-200 bg-[#FDFBF7] text-[#71717A]"
              data-testid="nudge-coming-soon-modal"
            >
              <Sparkles size={14} className="text-[#0A9699] flex-shrink-0" />
              <span className="text-xs font-medium leading-snug">
                Email nudges — coming soon
              </span>
            </div>
          )}

          {EMAIL_NUDGES_ENABLED && (nudgeSystem?.isSupported || !!userId) && (
            <button
              onClick={handleNudgeToggle}
              className={`w-full flex items-center gap-2 p-2 rounded-xl border transition-all ${
                nudgeEnabled
                  ? 'bg-[#E0F7FA] border-[#B2EBF2] text-[#00838F]'
                  : 'bg-[#FDFBF7] border-stone-200 text-stone-500 hover:border-[#B2EBF2] hover:bg-[#E0F7FA]/30'
              }`}
              data-testid="nudge-toggle-modal"
            >
              {nudgeEnabled ? <Bell size={14} /> : <BellOff size={14} className="opacity-60" />}
              <span className="text-xs font-medium">
                {nudgeEnabled ? "Email nudge enabled" : "Email nudge in 24 hrs"}
              </span>
              {nudgeEnabled && (
                <span className="ml-auto text-[10px] text-[#00838F]/70">
                  24 hrs
                </span>
              )}
            </button>
          )}

          {EMAIL_NUDGES_ENABLED && !nudgeSystem?.isSupported && !userId && (
            <button
              onClick={() => {
                onClose();
                onOpenInstallHelp?.();
              }}
              className="w-full flex items-center gap-2 p-2 rounded-xl border border-stone-200 bg-stone-50/50 text-stone-400 hover:bg-stone-100/50 hover:border-stone-300 transition-colors text-left"
              data-testid="nudge-install-help"
            >
              <BellOff size={14} className="opacity-50 flex-shrink-0" />
              <span className="text-xs">
                Nudges require <span className="text-[#0CBCBF] underline underline-offset-2">Add to Home Screen</span>
              </span>
            </button>
          )}

          {/* Lock Screen focus - shown for DOING and TO DO tasks (current focus + next up). */}
          {isInDoingList && (
            <button
              onClick={() => {
                const currentContent = (content || card?.content || "").trim();
                if (!currentContent) return;
                onClose();
                onOpenLockScreen?.(card.id, currentContent);
              }}
              className={`w-full flex items-center gap-2 p-2 rounded-xl border transition-all ${
                isLockScreenFocus
                  ? 'bg-[#E0F7FA] border-[#B8E6E7] text-[#00838F]'
                  : 'bg-[#FDFBF7] border-stone-200 text-stone-500 hover:border-[#B8E6E7] hover:bg-[#E0F7FA]/30'
              }`}
              data-testid="lockscreen-toggle-modal"
            >
              {isLockScreenFocus ? <Lock size={14} /> : <LockOpen size={14} className="opacity-70" />}
              <span className="text-xs font-medium">
                {isLockScreenFocus ? "This is your Lock Screen focus" : "Keep this on my Lock Screen"}
              </span>
              {isLockScreenFocus && (
                <span className="ml-auto text-[10px] text-[#00838F]/70">active</span>
              )}
            </button>
          )}

          <div>
            <label className="block text-xs font-medium text-[#52525B] mb-1">
              Task type
            </label>
            <CategorySelector value={category} onChange={handleCategoryChange} />
          </div>

          {(isEmailTask || isCallTask) && (
            <div>
              {!showContactInput ? (
                <button
                  onClick={() => setShowContactInput(true)}
                  className="w-full flex items-center gap-2 p-2 rounded-xl border border-dashed border-stone-300 bg-[#FDFBF7] hover:border-stone-400 hover:bg-stone-50/50 text-stone-500 transition-all"
                  data-testid="add-contact-btn"
                >
                  {isEmailTask ? <Mail size={14} /> : <Phone size={14} />}
                  <span className="text-xs font-medium">
                    {isEmailTask ? "Link email contact" : "Link phone contact"}
                  </span>
                </button>
              ) : (
                <div className="p-2.5 rounded-xl bg-stone-50/70 border border-stone-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-[#52525B]">
                      Linked contact
                    </label>
                    <button
                      onClick={handleRemoveContact}
                      className="text-[10px] text-red-400 hover:text-red-500 transition-colors"
                      data-testid="remove-contact-btn"
                    >
                      Remove
                    </button>
                  </div>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Name (optional)"
                    className="w-full bg-white rounded-lg px-2 py-1.5 text-sm border border-stone-200 outline-none focus:border-[#F58227] transition-colors"
                    data-testid="contact-name-input"
                  />
                  {isEmailTask && (
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="Email address"
                      className="w-full bg-white rounded-lg px-2 py-1.5 text-sm border border-stone-200 outline-none focus:border-[#F58227] transition-colors"
                      data-testid="contact-email-input"
                    />
                  )}
                  {isCallTask && (
                    <input
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="Phone number"
                      className="w-full bg-white rounded-lg px-2 py-1.5 text-sm border border-stone-200 outline-none focus:border-[#F58227] transition-colors"
                      data-testid="contact-phone-input"
                    />
                  )}
                </div>
              )}
            </div>
          )}

          <p className="text-[10px] text-[#A1A1AA]">
            Created: {new Date(card.createdAt).toLocaleString()}
          </p>

          {onShare && (
            <button
              onClick={() => {
                onClose();
                onShare(card);
              }}
              className="w-full flex items-center justify-center gap-2 p-2 rounded-xl border border-stone-200 bg-[#F6FAFA] hover:bg-[#E8F7F7] text-[#0CBCBF] transition-all"
              data-testid="share-from-edit-modal"
            >
              <Share size={14} />
              <span className="text-xs font-medium">Share this</span>
            </button>
          )}

          {onDelete && (
            <button
              onClick={() => {
                if (window.confirm('Delete this task?')) {
                  onDelete(card.id);
                  onClose();
                }
              }}
              className="flex items-center gap-1 px-2 py-0.5 text-red-300 hover:text-red-400 transition-all text-[11px]"
              data-testid="delete-from-edit-modal"
            >
              <Trash2 size={11} />
              <span>Delete task</span>
            </button>
          )}
        </div>

        <div className="sticky bottom-0 -mx-4 -mb-4 mt-3 px-4 pt-3 pb-4 bg-white flex gap-3 border-t border-stone-100">
          <button
            onClick={onClose}
            className="flex-1 py-2 text-stone-600 hover:text-stone-800 font-medium text-sm transition-colors"
            data-testid="edit-card-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 bg-[#F58227] text-white rounded-xl py-2 font-semibold text-sm hover:bg-[#D96D19] transition-colors"
            data-testid="edit-card-save"
          >
            Save
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Share App Modal (for desktop)
export const ShareAppModal = ({ onClose, onCopyLink, onEmail, onTwitter, onLinkedIn, onFacebook }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#1A1A1C]">Share Where Was I</h3>
          <button
            onClick={onClose}
            className="p-1 text-stone-400 hover:text-stone-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-sm text-stone-500 mb-5">
          A calm, focused task app for ADHD minds.
        </p>

        <div className="grid grid-cols-4 gap-3 mb-5">
          <button
            onClick={() => { onEmail(); onClose(); }}
            className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-stone-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail size={18} className="text-blue-600" />
            </div>
            <span className="text-xs text-stone-600">Email</span>
          </button>

          <button
            onClick={() => { onTwitter(); onClose(); }}
            className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-stone-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
              <Twitter size={18} className="text-sky-500" />
            </div>
            <span className="text-xs text-stone-600">Twitter</span>
          </button>

          <button
            onClick={() => { onLinkedIn(); onClose(); }}
            className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-stone-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Linkedin size={18} className="text-blue-700" />
            </div>
            <span className="text-xs text-stone-600">LinkedIn</span>
          </button>

          <button
            onClick={() => { onFacebook(); onClose(); }}
            className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-stone-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Facebook size={18} className="text-blue-600" />
            </div>
            <span className="text-xs text-stone-600">Facebook</span>
          </button>
        </div>

        <button
          onClick={() => { onCopyLink(); }}
          className="w-full flex items-center justify-center gap-2 py-3 bg-stone-100 hover:bg-stone-200 rounded-xl text-sm font-medium text-stone-700 transition-colors"
        >
          <Copy size={16} />
          Copy link
        </button>
      </motion.div>
    </div>
  );
};

// Find Thread Ritual - Breathing animation before scrolling to task
export const FindThreadRitual = ({ onComplete, taskName }) => {
  const [step, setStep] = useState(0);
  const soundPlayedRef = useRef({ swoosh: false, thump: false });

  useEffect(() => {
    if (!soundPlayedRef.current.swoosh) {
      soundPlayedRef.current.swoosh = true;
      playFocusSwooshSound();
    }

    const timer1 = setTimeout(() => setStep(1), 500);
    const timer2 = setTimeout(() => {
      setStep(2);
    }, 3000);
    const timer3 = setTimeout(() => onComplete(), 5200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(232, 237, 235, 0.97)' }}
    >
      <div className="text-center">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="reset"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col items-center"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, ease: "linear", repeat: Infinity }}
                className="w-12 h-12 rounded-full border-2 border-[#0CBCBF] border-t-transparent mb-4"
              />
              <p className="text-[#0CBCBF] font-medium text-lg">Reset</p>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="breathe"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center"
            >
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 2.5, ease: "easeInOut" }}
                className="w-32 h-32 rounded-full bg-[#0CBCBF]/15 flex items-center justify-center mb-6"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2.5, ease: "easeInOut" }}
                  className="w-20 h-20 rounded-full bg-[#0CBCBF]/30 flex items-center justify-center"
                >
                  <motion.div
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{ duration: 2.5, ease: "easeInOut" }}
                    className="w-10 h-10 rounded-full bg-[#0CBCBF]/50"
                  />
                </motion.div>
              </motion.div>
              <p className="text-[#0CBCBF] font-medium text-xl">Breathe</p>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="found"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="w-14 h-14 rounded-full bg-[#0CBCBF] flex items-center justify-center mb-4"
              >
                <Check className="text-white" size={28} />
              </motion.div>
              {taskName && (
                <p className="text-[17px] max-w-xs text-center">
                  <span className="text-[#F58227]">Here's where you were:</span> <span className="font-medium text-[#27272A]">{taskName}</span>
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
